import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { User, AuthCredentials, RegisterCredentials, apiClient } from '../lib/api'

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (credentials: AuthCredentials) => Promise<void>
  register: (credentials: RegisterCredentials) => Promise<void>
  logout: () => void
  refetchUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  // Check if user is already authenticated on mount
  useEffect(() => {
    checkAuthStatus()
  }, [])

  const checkAuthStatus = async () => {
    try {
      // Check if we have JWT token
      const token = localStorage.getItem('rml_jwt_token')
      if (!token) {
        setLoading(false)
        return
      }

      // Try to get current user with JWT
      const currentUser = await apiClient.getCurrentUser()
      setUser(currentUser)
    } catch (error) {
      console.error('Auth check failed:', error)

      // Only clear token if the error message indicates it's truly expired
      const errorMessage = error instanceof Error ? error.message : String(error)
      if (errorMessage.includes('Token expired') || errorMessage.includes('Authentication required')) {
        console.log('🔄 Clearing token due to expiration/auth required')
        apiClient.clearToken()
      } else {
        console.log('⚠️ Auth check failed but keeping token - may be temporary server issue')
      }
    } finally {
      setLoading(false)
    }
  }

  const login = async (credentials: AuthCredentials) => {
    setLoading(true)
    try {
      const authenticatedUser = await apiClient.login(credentials)
      setUser(authenticatedUser)
    } catch (error) {
      console.error('Login failed:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const register = async (credentials: RegisterCredentials) => {
    setLoading(true)
    try {
      const newUser = await apiClient.register(credentials)
      setUser(newUser)
    } catch (error) {
      console.error('Registration failed:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const logout = () => {
    apiClient.logout()
    setUser(null)
  }

  const refetchUser = async () => {
    try {
      const currentUser = await apiClient.getCurrentUser()
      setUser(currentUser)
    } catch (error) {
      console.error('Failed to refetch user:', error)

      // Only logout if it's a genuine authentication failure
      const errorMessage = error instanceof Error ? error.message : String(error)
      if (errorMessage.includes('Token expired') || errorMessage.includes('Authentication required')) {
        console.log('🔄 Logging out due to expired/missing authentication')
        logout()
      } else {
        console.log('⚠️ User refetch failed but keeping session - may be temporary issue')
      }
    }
  }

  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    logout,
    refetchUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export default AuthContext